import { cn } from '@/lib/utils';

type Props = {
  /** 7 entries, Monday..Sunday of the current week. */
  activity: boolean[];
  /**
   * 7 entries, parallel to `activity`: days declared off. A paused day reads as
   * a ring rather than a filled dot — neither done nor missed, because the
   * question was not asked. Optional so older callers still render.
   */
  paused?: boolean[];
  /** ISO date (YYYY-MM-DD) of the Monday this week represents. */
  weekStart: string;
};

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function WeekStrip({ activity, paused = [], weekStart }: Props) {
  // Compute today's index within Mon..Sun (0..6) using the same weekStart the API used.
  // We compare UTC-day differences to avoid TZ edge cases for the visual highlight.
  const monday = new Date(weekStart + 'T00:00:00Z');
  const now = new Date();
  const utcToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const dayDelta = Math.round((utcToday.getTime() - monday.getTime()) / 86_400_000);
  const todayIdx = dayDelta >= 0 && dayDelta < 7 ? dayDelta : -1;

  return (
    <section className="flex flex-col items-center gap-3 py-4">
      <h3 className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
        This week
      </h3>
      <ol className="flex gap-4">
        {activity.map((active, i) => {
          const off = paused[i] === true;
          // Practised on a day off: still a filled dot. The session happened.
          const state = active ? 'active' : off ? 'paused' : 'inactive';
          return (
            <li key={i} className="flex flex-col items-center gap-1.5" aria-label={DAY_NAMES[i]}>
              <span className="text-muted-foreground text-xs">{DAY_LABELS[i]}</span>
              <div
                className={cn(
                  'size-3 rounded-full transition-colors',
                  state === 'active' && 'bg-primary',
                  state === 'paused' && 'border-muted-foreground/50 border-2 bg-transparent',
                  state === 'inactive' && 'bg-border',
                  i === todayIdx && 'ring-ring ring-offset-background ring-2 ring-offset-2',
                )}
                aria-label={state === 'paused' ? 'paused' : state}
              />
            </li>
          );
        })}
      </ol>
    </section>
  );
}
